// Project Form Component

import { useState, useEffect } from 'react';
import type { Project, ProjectFormData, ProjectType, Customer } from '@/lib/types';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import { slugify } from '@/lib/utils';

interface ProjectFormProps {
  project: Project | null;
  customers: Customer[];
  preselectedCustomerId?: string;
  onSubmit: (data: ProjectFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}

export default function ProjectForm({
  project,
  customers,
  preselectedCustomerId,
  onSubmit,
  onCancel,
  isLoading,
}: ProjectFormProps) {
  const [formData, setFormData] = useState<ProjectFormData>({
    customer_id: preselectedCustomerId || '',
    name: '',
    slug: '',
    project_type: '',
    description: '',
    active: true,
    gsc_property: '',
    ads_account_id: '',
    business_context: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form with project data when editing
  useEffect(() => {
    if (project) {
      setFormData({
        customer_id: project.customer_id,
        name: project.name,
        slug: project.slug,
        project_type: project.project_type || '',
        description: project.description || '',
        active: project.active,
        gsc_property: project.gsc_property || '',
        ads_account_id: project.ads_account_id || '',
        business_context: project.business_context || '',
      });
    } else if (preselectedCustomerId) {
      setFormData(prev => ({ ...prev, customer_id: preselectedCustomerId }));
    }
  }, [project, preselectedCustomerId]);

  // Auto-generate slug from name + customer
  const handleNameChange = (name: string) => {
    const selectedCustomer = customers.find(c => c.id === formData.customer_id);
    const customerSlug = selectedCustomer?.slug || '';
    
    setFormData((prev) => ({
      ...prev,
      name,
      // Only auto-generate slug if creating new project
      slug: project ? prev.slug : `${customerSlug}-${slugify(name)}`.replace(/^-/, ''),
    }));
  };

  const handleCustomerChange = (customerId: string) => {
    const selectedCustomer = customers.find(c => c.id === customerId);
    const customerSlug = selectedCustomer?.slug || '';
    
    setFormData((prev) => ({
      ...prev,
      customer_id: customerId,
      // Update slug with new customer prefix
      slug: project ? prev.slug : `${customerSlug}-${slugify(prev.name)}`.replace(/^-/, ''),
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.customer_id) {
      newErrors.customer_id = 'Bitte wählen Sie einen Kunden';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    // Bewusst Pflicht ohne Vorbelegung: Eine falsch geratene Projektart faellt
    // erst im Monatsbericht auf (falsche Abschnitte, „Anfragen" statt
    // „Bewerbungen") — und dann ist der Bericht schon beim Kunden.
    if (!formData.project_type) {
      newErrors.project_type = 'Bitte die Projektart wählen';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug ist erforderlich';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug darf nur Kleinbuchstaben, Zahlen und Bindestriche enthalten';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Select
        label="Kunde"
        value={formData.customer_id}
        onChange={(e) => handleCustomerChange(e.target.value)}
        options={[
          { value: '', label: 'Kunde auswählen...' },
          ...customers.map((c) => ({
            value: c.id,
            label: c.name,
          })),
        ]}
        error={errors.customer_id}
        disabled={isLoading || !!preselectedCustomerId}
      />

      <Select
        label="Projektart"
        value={formData.project_type}
        onChange={(e) => setFormData({ ...formData, project_type: e.target.value as ProjectType | '' })}
        options={[
          { value: '', label: 'Bitte wählen …' },
          { value: 'landingpage', label: 'Landingpage + Google Ads' },
          { value: 'website', label: 'Komplette Website (SEO, Inhalte, Blog)' },
          { value: 'bewerbungen', label: 'Mitarbeitergewinnung (Meta-Ads)' },
        ]}
        error={errors.project_type}
        disabled={isLoading}
      />
      <p className="-mt-2 text-xs text-gray-500">
        Steuert den Monatsbericht: welche Kapitel entstehen (SEO und Blog nur bei kompletter Website),
        wie die Kontakte heißen (bei Mitarbeitergewinnung „Bewerbungen" statt „Anfragen") und welche
        fehlende Anbindung als Lücke gemeldet wird.
      </p>

      <Input
        label="Projektname"
        value={formData.name}
        onChange={(e) => handleNameChange(e.target.value)}
        placeholder="z.B. Landingpage Produkt A"
        required
        error={errors.name}
        disabled={isLoading}
      />

      <Input
        label="Slug"
        value={formData.slug}
        onChange={(e) => setFormData({ ...formData, slug: slugify(e.target.value) })}
        placeholder="z.B. kunde-produkt-a"
        required
        error={errors.slug}
        hint="Eindeutiger Identifier für API-Zugriff (nur Kleinbuchstaben, Zahlen, Bindestriche)"
        disabled={isLoading}
      />

      <Input
        label="Beschreibung (optional)"
        value={formData.description || ''}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        placeholder="z.B. Kontaktformular für Produkt A Landingpage"
        disabled={isLoading}
      />

      <Input
        label="Google Search Console Property (optional)"
        value={formData.gsc_property || ''}
        onChange={(e) => setFormData({ ...formData, gsc_property: e.target.value.trim() })}
        placeholder="z.B. sc-domain:schreinerei-thomas.de"
        hint="Für die SEO-Auswertung im Analytics-Bereich. Domain-Property: sc-domain:beispiel.de – URL-Property: https://www.beispiel.de/"
        disabled={isLoading}
      />

      <Input
        label="Google-Ads-Kundennummer (optional)"
        value={formData.ads_account_id || ''}
        onChange={(e) => setFormData({ ...formData, ads_account_id: e.target.value.replace(/\D/g, '').slice(0, 10) })}
        placeholder="z.B. 1234567890"
        hint="10 Ziffern (ohne Bindestriche). Ordnet die täglichen Google-Ads-Kosten aus dem MCC-Script diesem Projekt zu."
        disabled={isLoading}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Unternehmens-Steckbrief (für KI-Analysen)</label>
        <textarea
          value={formData.business_context || ''}
          onChange={(e) => setFormData({ ...formData, business_context: e.target.value })}
          rows={4}
          placeholder={'Ziel: … · Unternehmen: … · Zielgruppe: … · Dienstleistungen: … · Region: …'}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          disabled={isLoading}
        />
        <p className="mt-1 text-xs text-gray-500">
          Fließt als Strategie-Anker in alle KI-Analysen ein (Empfehlungen, Kampagnen-Analyse, Monats-Report):
          Was ist das Ziel, wer ist die Zielgruppe, welche Dienstleistungen zählen.
        </p>
      </div>

      <div className="flex items-center space-x-3">
        <input
          type="checkbox"
          id="active"
          checked={formData.active}
          onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          disabled={isLoading}
        />
        <label htmlFor="active" className="text-sm text-gray-700">
          Projekt ist aktiv
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
          Abbrechen
        </Button>
        <Button type="submit" loading={isLoading}>
          {project ? 'Speichern' : 'Erstellen'}
        </Button>
      </div>
    </form>
  );
}
